import React from 'react';
import { getGradeColor } from '../utils/creditBureauUtils';

interface ScoreGaugeProps {
  score: number;
  maxScore: number;
  grade: string;
  finalScore?: number;
  finalGrade?: string;
}

export const ScoreGauge: React.FC<ScoreGaugeProps> = ({ 
  score, 
  maxScore, 
  grade, 
  finalScore, 
  finalGrade 
}) => {
  const percentage = Math.min(100, Math.max(0, (score / maxScore) * 100));
  const finalPercentage = finalScore ? Math.min(100, Math.max(0, (finalScore / maxScore) * 100)) : percentage;
  
  // Calculate the stroke-dasharray for the circular progress
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
  const finalStrokeDasharray = finalScore ? `${(finalPercentage / 100) * circumference} ${circumference}` : strokeDasharray;
  
  return (
    <div className="flex flex-col items-center space-y-4">
      {/* Main Score Gauge */}
      <div className="relative">
        <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            stroke="currentColor"
            strokeWidth="8"
            fill="transparent"
            className="text-gray-200"
          />
          
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            stroke="currentColor"
            strokeWidth="8"
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeLinecap="round"
            className={`transition-all duration-1000 ease-out ${
              percentage >= 75 ? 'text-green-500' :
              percentage >= 60 ? 'text-blue-500' :
              percentage >= 45 ? 'text-yellow-500' :
              percentage >= 35 ? 'text-orange-500' : 'text-red-500'
            }`}
          />
          
          {/* Final score overlay if different */}
          {finalScore && finalScore !== score && (
            <circle
              cx="50"
              cy="50"
              r="38"
              stroke="currentColor"
              strokeWidth="4"
              fill="transparent"
              strokeDasharray={finalStrokeDasharray}
              strokeLinecap="round"
              className={`transition-all duration-1000 ease-out ${
                finalPercentage >= 75 ? 'text-green-400' :
                finalPercentage >= 60 ? 'text-blue-400' :
                finalPercentage >= 45 ? 'text-yellow-400' :
                finalPercentage >= 35 ? 'text-orange-400' : 'text-red-400'
              }`}
              opacity="0.7"
            />
          )}
        </svg>
        
        {/* Score text in center */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{score}</div>
            <div className="text-xs text-gray-500">/ {maxScore}</div>
          </div>
        </div>
      </div>
      
      {/* Grade badges */}
      <div className="text-center space-y-2">
        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getGradeColor(grade)}`}>
          {grade}
        </div>
        
        {finalScore && finalScore !== score && (
          <div className="text-center">
            <div className="text-xs text-gray-500 mb-1">Adjusted Score</div>
            <div className="flex items-center space-x-2">
              <span className="font-semibold text-gray-900">{finalScore}</span>
              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getGradeColor(finalGrade || '')}`}>
                {finalGrade}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Score ranges */}
      <div className="w-full max-w-sm">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Poor</span>
          <span>Fair</span>
          <span>Good</span>
          <span>Excellent</span>
        </div>
        <div className="h-2 bg-gradient-to-r from-red-500 via-yellow-500 via-blue-500 to-green-500 rounded-full"></div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>300</span>
          <span>600</span>
          <span>700</span>
          <span>850</span>
        </div>
      </div>
    </div>
  );
};
